// =============================================
// COMPONENTE: MetricsDashboard
// =============================================
// Arquivo: MetricsDashboard.tsx
// Descrição: Dashboard de métricas de automação
// =============================================

import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Activity,
  CheckCircle,
  XCircle,
  Clock,
  Users,
  Zap
} from 'lucide-react';
import { apiService } from '../../services/api';
import './MetricsDashboard.css';

interface Metrics {
  total_execucoes: number;
  execucoes_sucesso: number;
  execucoes_falha: number;
  execucoes_canceladas: number;
  tempo_medio_execucao: number;
  taxa_conversao: number;
  contatos_unicos: number;
  crescimento: number;
}

interface FlowMetrics {
  flow_id: number;
  flow_nome: string;
  total_execucoes: number;
  taxa_sucesso: number;
  tempo_medio: number;
}

const MetricsDashboard: React.FC = () => {
  const [metrics, setMetrics] = useState<Metrics>({
    total_execucoes: 0,
    execucoes_sucesso: 0,
    execucoes_falha: 0,
    execucoes_canceladas: 0,
    tempo_medio_execucao: 0,
    taxa_conversao: 0,
    contatos_unicos: 0,
    crescimento: 0
  });

  const [topFlows, setTopFlows] = useState<FlowMetrics[]>([]);
  const [period, setPeriod] = useState<'today' | 'week' | 'month'>('week');
  const [loading, setLoading] = useState(false);

  // =============================================
  // CARREGAR MÉTRICAS
  // =============================================

  const loadMetrics = async () => {
    setLoading(true);
    try {
      const response = await apiService.getAutomationMetrics({ periodo: period });
      if (response.success) {
        setMetrics(response.data.overall);
        setTopFlows(response.data.topFlows || []);
      }
    } catch (error) {
      // Erro tratado pelo apiService
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  // =============================================
  // UTILITÁRIOS
  // =============================================

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  const formatPercentage = (value: number) => {
    // Protege contra divisão por zero / valores inválidos (ex.: sem execuções no período)
    if (value === undefined || value === null || !Number.isFinite(value)) {
      return '0.0%';
    }
    return `${value.toFixed(1)}%`;
  };

  // =============================================
  // RENDERIZAÇÃO
  // =============================================

  return (
    <div className="metrics-dashboard">
      {/* HEADER */}
      <div className="metrics-header">
        <div className="metrics-title">
          <TrendingUp size={24} />
          <h2>Métricas de Automação</h2>
        </div>

        <div className="period-selector">
          <button
            className={period === 'today' ? 'active' : ''}
            onClick={() => setPeriod('today')}
          >
            Hoje
          </button>
          <button
            className={period === 'week' ? 'active' : ''}
            onClick={() => setPeriod('week')}
          >
            7 Dias
          </button>
          <button
            className={period === 'month' ? 'active' : ''}
            onClick={() => setPeriod('month')}
          >
            30 Dias
          </button>
        </div>
      </div>

      {/* CARDS DE MÉTRICAS */}
      <div className="metrics-grid">
        {/* Total de Execuções */}
        <div className="metric-card">
          <div className="metric-icon" style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}>
            <Activity size={24} />
          </div>
          <div className="metric-content">
            <span className="metric-label">Total de Execuções</span>
            <span className="metric-value">{metrics.total_execucoes.toLocaleString()}</span>
            {metrics.crescimento !== 0 && (
              <span className={`metric-change ${metrics.crescimento > 0 ? 'positive' : 'negative'}`}>
                {metrics.crescimento > 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                {Math.abs(metrics.crescimento)}%
              </span>
            )}
          </div>
        </div>

        {/* Taxa de Sucesso */}
        <div className="metric-card">
          <div className="metric-icon" style={{ background: 'linear-gradient(135deg, #48bb78, #38a169)' }}>
            <CheckCircle size={24} />
          </div>
          <div className="metric-content">
            <span className="metric-label">Taxa de Sucesso</span>
            <span className="metric-value">{formatPercentage(metrics.taxa_conversao)}</span>
            <span className="metric-detail">
              {metrics.execucoes_sucesso} de {metrics.total_execucoes} execuções
            </span>
          </div>
        </div>

        {/* Falhas */}
        <div className="metric-card">
          <div className="metric-icon" style={{ background: 'linear-gradient(135deg, #fc8181, #f56565)' }}>
            <XCircle size={24} />
          </div>
          <div className="metric-content">
            <span className="metric-label">Execuções Falhadas</span>
            <span className="metric-value">{metrics.execucoes_falha}</span>
            <span className="metric-detail">
              {formatPercentage((metrics.execucoes_falha / metrics.total_execucoes) * 100)}
            </span>
          </div>
        </div>

        {/* Tempo Médio */}
        <div className="metric-card">
          <div className="metric-icon" style={{ background: 'linear-gradient(135deg, #4299e1, #3182ce)' }}>
            <Clock size={24} />
          </div>
          <div className="metric-content">
            <span className="metric-label">Tempo Médio</span>
            <span className="metric-value">{formatDuration(metrics.tempo_medio_execucao)}</span>
            <span className="metric-detail">por execução</span>
          </div>
        </div>

        {/* Contatos Únicos */}
        <div className="metric-card">
          <div className="metric-icon" style={{ background: 'linear-gradient(135deg, #9f7aea, #805ad5)' }}>
            <Users size={24} />
          </div>
          <div className="metric-content">
            <span className="metric-label">Contatos Únicos</span>
            <span className="metric-value">{metrics.contatos_unicos}</span>
            <span className="metric-detail">usuários atendidos</span>
          </div>
        </div>

        {/* Canceladas */}
        <div className="metric-card">
          <div className="metric-icon" style={{ background: 'linear-gradient(135deg, #ed8936, #dd6b20)' }}>
            <XCircle size={24} />
          </div>
          <div className="metric-content">
            <span className="metric-label">Canceladas</span>
            <span className="metric-value">{metrics.execucoes_canceladas}</span>
            <span className="metric-detail">
              {formatPercentage((metrics.execucoes_canceladas / metrics.total_execucoes) * 100)}
            </span>
          </div>
        </div>
      </div>

      {/* TOP FLUXOS */}
      <div className="top-flows">
        <h3>
          <Zap size={20} />
          Top Fluxos Mais Executados
        </h3>

        {loading ? (
          <div className="loading-state">
            <Activity size={32} className="spinning" />
            <p>Carregando métricas...</p>
          </div>
        ) : topFlows.length === 0 ? (
          <div className="empty-state">
            <Activity size={48} />
            <p>Nenhuma execução ainda</p>
          </div>
        ) : (
          <div className="flows-list">
            {topFlows.map((flow, index) => (
              <div key={flow.flow_id} className="flow-item">
                <div className="flow-rank">#{index + 1}</div>
                <div className="flow-info">
                  <span className="flow-name">{flow.flow_nome}</span>
                  <span className="flow-stats">
                    {flow.total_execucoes} execuções • {formatPercentage(flow.taxa_sucesso)} sucesso
                  </span>
                </div>
                <div className="flow-metric">
                  <Clock size={14} />
                  <span>{formatDuration(flow.tempo_medio)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MetricsDashboard;
